// Control: één cockpit boven de OS-modules.
// Wat wacht op jou, wat kost het vandaag, welke module staat aan of uit, wat levert elke module op, en klopt het logboek.
// Bouwt op de bestaande goedkeuringswachtrij, kosten-ledgers en het hash-logboek. Ontwerp: docs/Ontwerp_Control.md
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase";
import { C, Kop, Tellers, Groep, Rij, Leeg, Chip, Melding, eur } from "./gedeeld";
import { leesModusActief } from "./leesModus";
import { kostenKleur, statusKleur, statusTekst, omzetPerModule, maandLabel, TIER_TEKST } from "./controlHulp";

const dtm = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const maandStart = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-01"; };

export default function Control({ setView }) {
  const [sam, setSam] = useState(null);
  const [fout, setFout] = useState(null);
  const [toast, setToast] = useState(null);
  const [bezig, setBezig] = useState(null);
  const [omzet, setOmzet] = useState({ module: "", bedrag: "", notitie: "" });
  const gast = leesModusActief();

  const melding = (m) => { setToast(m); setTimeout(() => setToast(null), 3500); };

  const laad = useCallback(async () => {
    const { data, error } = await supabase.rpc("control_samenvatting");
    if (error) { setFout(/does not exist|schema cache/i.test(error.message) ? "migratie" : error.message); return; }
    setFout(null);
    setSam(data);
  }, []);

  useEffect(() => {
    laad();
    const t = setInterval(laad, 30000);
    return () => clearInterval(t);
  }, [laad]);

  async function zetStatus(m, status) {
    let reden = null;
    if (status !== "actief") {
      const tekst = status === "stilgezet" ? "Weet je zeker dat je " + m.naam + " stilzet?" : "Pauzeer " + m.naam + "?";
      if (!window.confirm(tekst)) return;
      reden = window.prompt("Reden (optioneel)", "") || null;
    }
    setBezig(m.slug);
    const { error } = await supabase.rpc("control_zet_status", { p_slug: m.slug, p_status: status, p_reden: reden });
    setBezig(null);
    if (error) { melding("Niet gelukt: " + error.message); return; }
    melding(m.naam + ": " + statusTekst(status));
    laad();
  }

  async function bewaarOmzet() {
    const bedrag = Number(String(omzet.bedrag).replace(",", "."));
    if (!omzet.module || !Number.isFinite(bedrag) || bedrag < 0) { melding("Kies een module en vul een bedrag in."); return; }
    setBezig("omzet");
    const { error } = await supabase.rpc("control_zet_omzet", { p_slug: omzet.module, p_maand: maandStart(), p_bedrag: bedrag, p_notitie: omzet.notitie || null });
    setBezig(null);
    if (error) { melding("Niet gelukt: " + error.message); return; }
    setOmzet({ module: "", bedrag: "", notitie: "" });
    melding("Omzet opgeslagen.");
    laad();
  }

  if (fout === "migratie") {
    return <div><Kop titel="Control" /><Melding type="let-op"><strong>Eenmalige database-stap nog nodig.</strong> De migratie <code>control_laag.sql</code> is nog niet uitgevoerd.</Melding></div>;
  }
  if (fout) return <div><Kop titel="Control" /><Melding type="fout">{fout}</Melding></div>;
  if (!sam) return <div><Kop titel="Control" /><Leeg tekst="Laden..." /></div>;

  const { wachtrij, kosten, modules, beleid, logboek } = sam;
  const keten = logboek && logboek.keten;
  const kk = kostenKleur(kosten.pct);
  const nietActief = modules.filter((m) => m.status !== "actief").length;
  const omzetRijen = omzetPerModule(sam.omzet || [], modules);

  return (
    <div className="fu">
      <Kop titel="Control" sub="Eén plek voor wat op jou wacht, wat het kost, wat aan staat en of het logboek klopt." />
      {toast && <Melding type="info">{toast}</Melding>}
      {kosten.pct != null && kosten.pct >= 100 && <Melding type="fout">De gezamenlijke dagelijkse limiet is bereikt. Claude-aanroepen via de OS worden nu geweigerd tot morgen. Functies die buiten Claude om werken (zoals Gemini, OpenAI of Groq) stoppen hier niet vanzelf; pauzeer ze waar nodig.</Melding>}
      {kosten.pct != null && kosten.pct >= 80 && kosten.pct < 100 && <Melding type="let-op">Je zit op {Math.round(kosten.pct)}% van de dagelijkse AI-limiet.</Melding>}
      {keten && keten.ok === false && <Melding type="fout">Het logboek is niet meer sluitend vanaf regel {keten.eerste_fout_seq}. Onderzoek dit voordat je iets goedkeurt.</Melding>}

      <Tellers items={[
        { label: "Wacht op jou", waarde: wachtrij.open, kleur: wachtrij.open ? C.yellow : C.green, onClick: setView ? () => setView("goedkeuringen") : undefined },
        { label: "Kosten vandaag", waarde: eur(kosten.totaal), kleur: kk },
        { label: "Limiet", waarde: kosten.pct == null ? "geen" : Math.round(kosten.pct) + "%", kleur: kk },
        { label: "Modules uit", waarde: nietActief, kleur: nietActief ? C.red : C.green },
      ]} />

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Groep label="Modules en noodknop" kleur={C.blue} aantal={modules.length}>
          {modules.map((m) => (
            <Rij key={m.slug} kleur={statusKleur(m.status)} titel={m.naam}
              meta={<>
                <Chip tekst={statusTekst(m.status)} kleur={statusKleur(m.status)} />
                <Chip tekst={m.afgedwongen ? "slot werkt" : "nog geen slot"} kleur={m.afgedwongen ? C.green : C.muted} />
                {m.reden && <span>{m.reden}</span>}
                {m.bijgewerkt_door && m.status !== "actief" && <span>{dtm(m.bijgewerkt_op)}</span>}
              </>}
              rechts={!gast && (
                <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                  {m.status !== "actief" && <button className="btn" disabled={bezig === m.slug} onClick={() => zetStatus(m, "actief")}>Hervat</button>}
                  {m.status === "actief" && <button className="btn" disabled={bezig === m.slug} onClick={() => zetStatus(m, "gepauzeerd")}>Pauzeer</button>}
                  {m.status !== "stilgezet" && <button className="btn" disabled={bezig === m.slug} onClick={() => zetStatus(m, "stilgezet")}>Zet stil</button>}
                </div>
              )} />
          ))}
          <div style={{ padding: "8px 12px", fontSize: 11, color: C.muted, borderTop: "1px solid " + C.border }}>
            "Slot werkt" betekent dat de code van die module de schakelaar echt controleert. Bij de andere is dit nu een signaal; ze worden één voor één aangesloten.
          </div>
        </Groep>

        <Groep label="Kosten vandaag" kleur={kk} aantal={eur(kosten.totaal) + " van " + eur(kosten.limiet)}>
          {(kosten.per_bron || []).length === 0 ? <Leeg tekst="Nog geen kosten vandaag." /> : kosten.per_bron.map((k) => (
            <Rij key={k.bron} titel={k.label} rechts={<span style={{ fontFamily: "monospace", fontSize: 13 }}>{eur(k.eur)}</span>} />
          ))}
          <div style={{ padding: "8px 12px", fontSize: 11, color: C.muted, borderTop: "1px solid " + C.border }}>
            Gezamenlijke limiet: {eur(kosten.limiet)} per dag. Bij 80% schakelt de Claude-chat terug naar het goedkoopste model en krijg je een melding, bij 100% weigert hij nieuwe aanroepen. Directe aanroepen naar andere aanbieders vallen hier niet onder.
          </div>
        </Groep>

        <Groep label="Wacht op jou" kleur={C.yellow} aantal={wachtrij.open}>
          {wachtrij.open === 0 ? <Leeg tekst="Niets dat wacht." /> : (
            <Rij titel={wachtrij.open + " voorstellen, waarvan " + wachtrij.extern + " naar buiten"}
              meta={<span>Oudste: {dtm(wachtrij.oudste)}</span>}
              onKlik={setView ? () => setView("goedkeuringen") : undefined} />
          )}
        </Groep>

        <Groep label="Omzet per module" kleur={C.green} standaardDicht aantal={omzetRijen.length}>
          {omzetRijen.length === 0 ? <Leeg tekst="Nog geen omzet ingevoerd. Je voert die hieronder zelf in, per maand." /> : omzetRijen.map((r) => (
            <Rij key={r.slug} titel={r.naam} meta={<span>{r.maanden.map((x) => maandLabel(x.maand) + " " + eur(x.bedrag_eur)).join("  ·  ")}</span>} rechts={<span style={{ fontFamily: "monospace", fontSize: 13 }}>{eur(r.dezeMaand)}</span>} />
          ))}
          {!gast && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: 12, borderTop: "1px solid " + C.border }}>
              <select className="inp" value={omzet.module} onChange={(e) => setOmzet({ ...omzet, module: e.target.value })}>
                <option value="">Module...</option>
                {modules.map((m) => <option key={m.slug} value={m.slug}>{m.naam}</option>)}
              </select>
              <input className="inp" placeholder="Omzet deze maand (€)" inputMode="decimal" value={omzet.bedrag} onChange={(e) => setOmzet({ ...omzet, bedrag: e.target.value })} style={{ width: 170 }} />
              <input className="inp" placeholder="Notitie (optioneel)" value={omzet.notitie} onChange={(e) => setOmzet({ ...omzet, notitie: e.target.value })} style={{ flex: 1, minWidth: 140 }} />
              <button className="btn" disabled={bezig === "omzet"} onClick={bewaarOmzet}>Opslaan</button>
            </div>
          )}
        </Groep>

        <Groep label="Beleid per soort actie" kleur={C.muted} standaardDicht aantal={beleid.length}>
          {beleid.map((b) => (
            <Rij key={b.actie} titel={b.actie} meta={<><Chip tekst={"T" + b.tier} kleur={b.tier >= 3 ? C.red : b.tier === 2 ? C.yellow : C.green} /><span>{TIER_TEKST[b.tier]}</span></>}
              rechts={<Chip tekst={b.afdwinging === "afgedwongen" ? "afgedwongen" : "beschrijvend"} kleur={b.afdwinging === "afgedwongen" ? C.green : C.muted} />} />
          ))}
        </Groep>

        <Groep label="Logboek" kleur={keten && keten.ok ? C.green : C.red} standaardDicht aantal={keten ? keten.regels + " regels" : ""}>
          <div style={{ padding: "8px 12px", fontSize: 12, color: C.muted }}>
            Hashketen: {keten && keten.ok ? "sluitend" : "NIET sluitend"}. Regels kunnen niet worden gewijzigd of verwijderd.
          </div>
          {(logboek.laatste || []).map((l) => (
            <Rij key={l.seq} titel={l.bron + " · " + l.actie} meta={<><span>#{l.seq}</span><span>{dtm(l.at)}</span><span>{l.actor}</span></>} />
          ))}
        </Groep>
      </div>
    </div>
  );
}
